import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { ControlledPasswordField } from '@/common/components/Fields/ControlledPasswordField/ControlledPasswordField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { RouterLink } from '@/common/components/RouterLink/RouterLink';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { ROUTES } from '@/common/constants/routes';
import { ResetPasswordModal } from '@/common/containers/Authentication/components/ResetPasswordModal/ResetPasswordModal';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { useAuthenticationContext } from '@/common/contexts/authentication/AuthenticationContext';
import { useBiometrics } from '@/common/hooks/useBiometrics';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { AuthService } from '@/common/integrations/menhir/employee/sessions/sessions.service';
import { SecureStorageService } from '@/common/integrations/secure-storage/secure-storage.service';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import errorUtils from '@/common/utils/error';
import { notify } from '@/common/utils/toast/toast';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ILoginFormData, formSchema } from './SignInPage.schema';
import { styles } from './SignInPage.styles';
import { SessionExpiredModal } from './components/SessionExpiredModal/SessionExpiredModal';

export function SignInPage() {
  usePageViewTracking('login_page');
  const { t } = useTranslation();
  const router = useRouter();
  const { signIn } = useAuth();
  const { setContextCredentials } = useAuthenticationContext();
  const breakpoints = useBreakpoints(['lg']);
  const searchParams = useLocalSearchParams();
  const { biometricsAvailable } = useBiometrics();
  const [sessionExpiredVisible, setSessionExpiredVisible] = useState(false);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async ({ email, password }: ILoginFormData) => {
    try {
      const signInResponse = await AuthService.signIn({
        email,
        password,
      });

      setContextCredentials({
        email,
        password,
        authState: signInResponse,
      });

      await SecureStorageService.saveDataToSecureStorage({
        email,
        password,
      });

      if ('reason' in signInResponse) {
        if (signInResponse.reason === 'OTP_REQUIRED') {
          AnalyticsService.trackCustomEvent('login_otp_required');
          notify({
            type: 'success',
            message: t('authentication.signin.form.mfa.otp.title', {
              suffix: signInResponse.phoneLastDigits,
            }),
          });
          router.navigate(ROUTES.OTP);
        }
        return;
      }
      if ('user' in signInResponse) {
        const { user } = signInResponse;

        if (biometricsAvailable) return router.navigate(ROUTES.BIOMETRICS);
        if (Platform.OS !== 'web') {
          return router.navigate(ROUTES.NOTIFICATIONS);
        }

        await signIn(signInResponse);

        AnalyticsService.trackCustomEvent('login_success', {
          employee_id: user.id,
          employee_email: user.email,
          company_id: user.companyId,
        });
      }
    } catch (err: any) {
      const errorMessage = errorUtils.getErrorMessageKey(err?.errorCode);
      notify({
        type: 'error',
        message: t(errorMessage),
      });
      AnalyticsService.trackCustomEvent('login_failed');
    }
  };

  useEffect(() => {
    if (searchParams.sessionExpired) {
      AnalyticsService.trackCustomEvent('login_session_expired');
      setSessionExpiredVisible(true);
    }
  }, [router, searchParams.sessionExpired]);

  const actions = (inLayout: boolean) => (
    <Stack
      horizontal
      gap="lg"
      align="center"
      wrap
      className={inLayout ? styles.actions : ''}
    >
      <TextAction size="sm" onPress={() => setResetPasswordVisible(true)}>
        {t('authentication.signin.forgot_password')}
      </TextAction>
      <RouterLink href={ROUTES.SSO_SIGNIN}>
        <BodyText underline bold size="sm">
          {t('authentication.signin.sso_link')}
        </BodyText>
      </RouterLink>
      {Platform.OS === 'web' && (
        <TextAction size="sm" href={process.env.EXPO_PUBLIC_APOLLO_URL}>
          {t('authentication.signin.company_link')}
        </TextAction>
      )}
    </Stack>
  );

  return (
    <AuthFormLayout
      footer={
        <>
          <Button
            type="submit"
            onPress={methods.handleSubmit(onSubmit)}
            loading={methods.formState.isSubmitting}
          >
            {t('authentication.signin.form.button')}
          </Button>
          {actions(true)}
        </>
      }
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap="xl">
            <PageHeader title={t('authentication.signin.form.title')} />
            <Stack gap="md">
              <ControlledTextField
                name="email"
                type="email"
                label={t('authentication.signin.form.email.label')}
              />
              <ControlledPasswordField
                name="password"
                label={t('authentication.signin.form.password.label')}
              />
            </Stack>
            {!breakpoints.lg && actions(false)}
          </Stack>
        </Form>
      </FormProvider>
      {resetPasswordVisible && (
        <ResetPasswordModal onClose={() => setResetPasswordVisible(false)} />
      )}
      {sessionExpiredVisible && (
        <SessionExpiredModal onClose={() => setSessionExpiredVisible(false)} />
      )}
    </AuthFormLayout>
  );
}
